import type { FulfillmentOrderDetail, FulfillmentOrderLine } from '@/infrastructure/FulfillmentAdminClient';
import { canInboundLine, isOutboundReady } from '@/utils/fulfillmentLineStatus';

const CANCELLED_STORE_STATUS = 'cancelled';

/** A cancelled order is out of the inbound flow, whatever its lines still read, so nothing on it is received [SSD-7049]. */
export const isCancelledOrder = (header: FulfillmentOrderDetail['header']): boolean =>
  header.fulfillmentStatus === 'CANCELLED' || (header.wcStatus ?? '').toLowerCase() === CANCELLED_STORE_STATUS;

/** One order of a shared waybill's run and the line of it a scanned product belongs to. */
export interface RunLine {
  detail: FulfillmentOrderDetail;
  line: FulfillmentOrderLine;
}

const placedAt = (detail: FulfillmentOrderDetail): number => {
  const at = Date.parse(detail.header.orderPlacedAt ?? '');
  return Number.isNaN(at) ? Number.POSITIVE_INFINITY : at;
};

/** Oldest placed first; an order with no readable placed time goes last, and the order number breaks a tie. */
const byOldest = (a: RunLine, b: RunLine): number =>
  placedAt(a.detail) - placedAt(b.detail) || a.detail.header.orderId - b.detail.header.orderId;

const firstLine = (detail: FulfillmentOrderDetail, sku: string, wanted: (lineStatus: string) => boolean) =>
  [...detail.lines].sort((a, b) => a.lineNo - b.lineNo).find((line) => line.sku === sku && wanted(line.lineStatus));

/**
 * Where a product scanned against a shared waybill's run is received: the oldest placed order of the run that
 * still waits for that SKU [SSD-7413]. Each scan reads the run afresh, so the next unit of the same SKU goes to
 * the next-oldest order once this one is in. A cancelled order is never a candidate.
 */
export const oldestWaitingLine = (details: FulfillmentOrderDetail[], sku: string): RunLine | undefined =>
  details
    .filter((detail) => !isCancelledOrder(detail.header))
    .flatMap((detail) => {
      const line = firstLine(detail, sku, canInboundLine);
      return line ? [{ detail, line }] : [];
    })
    .sort(byOldest)[0];

/**
 * A run order whose line for this SKU is already in; a second scan of the same unit names it rather than receiving
 * again. A cancelled order is no home for the unit, so it is never named.
 */
export const arrivedLine = (details: FulfillmentOrderDetail[], sku: string): RunLine | undefined =>
  details
    .filter((detail) => !isCancelledOrder(detail.header))
    .flatMap((detail) => {
      const line = firstLine(detail, sku, isOutboundReady);
      return line ? [{ detail, line }] : [];
    })
    .sort(byOldest)[0];
